/**
 * The small pieces every community view shares: post text, attachment cards,
 * badges, buttons. `docs/plans/community.md`; the mock is
 * `docs/community-mock.html`.
 *
 * **Text is rendered as text.** A post is split into plain segments
 * (`lib/community.ts` `tokenize`) and drawn as React text nodes — there is no
 * `innerHTML` anywhere here, so nothing a member or a visitor types can run.
 */

import { useState, type CSSProperties, type ReactNode } from "react";
import type { Layout } from "@qufolio/model";
import { mintUlid } from "@qufolio/model";

import { imageSrc, type Embed } from "../../api/community";
import { fetchAccount, savePlan } from "../../api/journal";
import { createScreen } from "../../api/presets";
import { planR, layoutFromChartEmbed, sharedChartName } from "../../lib/community";
import { COLOUR_VAR, parsePost, type Inline } from "../../lib/community-format";
import { loadScreens } from "../../lib/screens";
import { colourToken, createList } from "../../lib/track-lists";
import { useTokenResolver } from "../theme";

export interface OpenActions {
  onOpenSymbol: (symbol: string) => void;
  onOpenLayout: (definition: Partial<Layout>, name: string) => void;
}

// ── text ─────────────────────────────────────────────────────────────────────

/**
 * A post, formatted — `lib/community-format.ts` parses, this draws.
 *
 * Every node becomes a React element built here; nothing is set as HTML, so
 * the only formats that can appear are the ones this function knows.
 */
export function PostText({ text, onSymbol }: { text: string; onSymbol: (symbol: string) => void }) {
  const inline = (nodes: Inline[], key = ""): ReactNode[] =>
    nodes.map((n, i) => {
      const k = `${key}${i}`;
      switch (n.kind) {
        case "text":
          return <span key={k}>{n.text}</span>;
        case "cashtag":
          return (
            <button key={k} type="button" onClick={() => onSymbol(n.symbol)} title={`Chart ${n.symbol}`} style={CASHTAG}>
              ${n.symbol}
            </button>
          );
        case "link":
          return (
            <a key={k} href={n.href} target="_blank" rel="noopener noreferrer nofollow" style={{ color: "var(--qf-accent)" }}>
              {inline(n.children, `${k}.`)}
            </a>
          );
        case "bold":
          return <strong key={k}>{inline(n.children, `${k}.`)}</strong>;
        case "italic":
          return <em key={k}>{inline(n.children, `${k}.`)}</em>;
        case "colour":
          return <span key={k} style={{ color: COLOUR_VAR[n.colour] }}>{inline(n.children, `${k}.`)}</span>;
        case "small":
          return <span key={k} style={{ fontSize: "0.85em", color: "var(--qf-text-secondary)" }}>{inline(n.children, `${k}.`)}</span>;
      }
    });
  const lines = (rows: Inline[][], key: string) =>
    rows.map((row, i) => (
      <span key={`${key}${i}`}>
        {i > 0 && <br />}
        {inline(row, `${key}${i}.`)}
      </span>
    ));
  return (
    <div style={{ wordBreak: "break-word", fontSize: 13, lineHeight: 1.45 }}>
      {parsePost(text).map((block, i) => {
        switch (block.kind) {
          case "heading":
            return (
              <div key={i} role="heading" aria-level={block.level + 1} style={{ fontWeight: 700, margin: "6px 0 3px", fontSize: HEADING_SIZE[block.level] }}>
                {inline(block.children, `h${i}.`)}
              </div>
            );
          case "paragraph":
            return <p key={i} style={{ margin: "0 0 6px" }}>{lines(block.lines, `p${i}.`)}</p>;
          case "list": {
            const items = block.items.map((item, j) => <li key={j}>{inline(item, `l${i}.${j}.`)}</li>);
            return block.ordered ? (
              <ol key={i} style={{ margin: "0 0 6px", paddingLeft: 20 }}>{items}</ol>
            ) : (
              <ul key={i} style={{ margin: "0 0 6px", paddingLeft: 18 }}>{items}</ul>
            );
          }
          case "quote":
            return (
              <blockquote key={i} style={{ margin: "0 0 6px", padding: "2px 0 2px 10px", borderLeft: "3px solid var(--qf-border)", color: "var(--qf-text-secondary)" }}>
                {lines(block.lines, `q${i}.`)}
              </blockquote>
            );
        }
      })}
    </div>
  );
}

const HEADING_SIZE: Record<1 | 2 | 3, number> = { 1: 18, 2: 15.5, 3: 13.5 };

const CASHTAG: CSSProperties = {
  border: 0,
  padding: 0,
  background: "none",
  color: "var(--qf-accent)",
  fontWeight: 600,
  font: "inherit",
  cursor: "pointer",
};

// ── chrome ───────────────────────────────────────────────────────────────────

export function Btn({
  children,
  onClick,
  primary,
  danger,
  disabled,
  title,
  small,
}: {
  children: ReactNode;
  onClick?: () => void;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        fontSize: small ? 11 : 12,
        padding: small ? "1px 7px" : "3px 10px",
        borderRadius: 5,
        border: `1px solid ${danger ? "var(--qf-negative)" : primary ? "var(--qf-accent)" : "var(--qf-border)"}`,
        background: primary ? "var(--qf-accent)" : "var(--qf-surface)",
        color: primary ? "var(--qf-background)" : danger ? "var(--qf-negative)" : "var(--qf-text)",
        fontWeight: primary ? 600 : 400,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

export function Pill({ on, children, onClick }: { on?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        fontSize: 11.5,
        padding: "1px 9px",
        borderRadius: 11,
        border: `1px solid ${on ? "var(--qf-accent)" : "var(--qf-border)"}`,
        background: "none",
        color: on ? "var(--qf-accent)" : "var(--qf-text-secondary)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

export function TopicDot({ colour }: { colour: string }) {
  const resolve = useTokenResolver();
  return (
    <span
      aria-hidden
      style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: resolve(colourToken(colour)), flex: "none" }}
    />
  );
}

export function Badge({ kind }: { kind: "admin" | "host" | "visitor" | "op" | "pending" | "pinned" | "locked" | "edit" }) {
  const text = {
    admin: "Group Admin",
    host: "Host",
    visitor: "Visitor",
    op: "OP",
    pending: "⏳ Awaiting approval",
    pinned: "📌 Pinned",
    locked: "🔒 Locked",
    edit: "✎ Edit awaiting approval",
  }[kind];
  const warn = kind === "pending" || kind === "edit";
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        padding: "0 5px",
        borderRadius: 3,
        marginRight: 5,
        whiteSpace: "nowrap",
        color: warn ? "var(--qf-warning, var(--qf-warn))" : kind === "pinned" ? "var(--qf-warn)" : "var(--qf-accent)",
        border: `1px solid ${warn ? "var(--qf-warn)" : "var(--qf-border)"}`,
      }}
    >
      {text}
    </span>
  );
}

export const META: CSSProperties = { fontSize: 11.5, color: "var(--qf-text-secondary)" };

export function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return <div role="alert" style={{ fontSize: 12, color: "var(--qf-negative)", margin: "4px 0" }}>{error}</div>;
}

// ── attachments ──────────────────────────────────────────────────────────────

/**
 * One attachment, drawn from its copy.
 *
 * The image is what the author saw; **Open live** applies the copy as a new
 * layout with today's bars, under the reader's own plan — an indicator the
 * reader is not granted draws as a locked row there, exactly as it would if
 * they added it themselves.
 */
export function EmbedCard({ embed, author, actions }: { embed: Embed; author: string; actions: OpenActions }) {
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = (fn: () => Promise<string>) => async () => {
    setBusy(true);
    try {
      setNote(await fn());
    } catch (cause) {
      setNote(cause instanceof Error ? cause.message : "that did not work");
    } finally {
      setBusy(false);
    }
  };

  let head: ReactNode;
  let body: ReactNode = null;
  let action: ReactNode = null;
  switch (embed.kind) {
    case "chart": {
      const def = layoutFromChartEmbed(embed);
      head = (
        <>
          📷 <b style={{ color: "var(--qf-text)" }}>{embed.symbol}</b>
          {embed.interval ? ` · ${embed.interval}` : ""}
          {embed.indicators?.length ? ` · ${embed.indicators.join(", ")}` : ""}
        </>
      );
      body = embed.imageUrl ? (
        <ZoomImage src={imageSrc(embed.imageUrl)!} alt={`${embed.symbol} chart as ${author} saw it`} />
      ) : null;
      action = (
        <>
          <Btn small onClick={() => actions.onOpenSymbol(embed.symbol)}>Chart {embed.symbol}</Btn>
          {def && (
            <Btn small primary onClick={() => actions.onOpenLayout(def, sharedChartName(embed, author))} title="Adds it as a new layout tab, with today's bars and your own plan's indicators">
              Open live
            </Btn>
          )}
        </>
      );
      break;
    }
    case "layout":
      head = <>▦ Layout · <b style={{ color: "var(--qf-text)" }}>{embed.name}</b> · {(embed.symbols ?? []).join(", ")}</>;
      body = embed.imageUrls?.length ? (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(2, embed.imageUrls.length)}, 1fr)`, gap: 4 }}>
          {embed.imageUrls.map((url, i) => (
            <ZoomImage key={i} src={imageSrc(url)!} alt={`${embed.symbols?.[i] ?? "chart"} as ${author} saw it`} />
          ))}
        </div>
      ) : null;
      action = (
        <Btn small primary onClick={() => actions.onOpenLayout(embed.layout as Partial<Layout>, `${embed.name} (${author})`.slice(0, 48))}>
          Open as layout
        </Btn>
      );
      break;
    case "screen":
      head = <>▦ Screen · <b style={{ color: "var(--qf-text)" }}>{embed.name}</b></>;
      action = (
        <Btn
          small
          primary
          disabled={busy}
          onClick={run(async () => {
            const c = embed.config as Record<string, unknown>;
            await createScreen({
              name: `${embed.name} (${author})`.slice(0, 48),
              market: (c.market as string) ?? "US",
              groupCodes: (c.groupCodes as string[]) ?? [],
              columns: (c.columns as never) ?? [],
              filter: (c.filter as never) ?? undefined,
              sort: (c.sort as never) ?? [],
            });
            await loadScreens();
            return "Copied to your screens — pick it in the screener's tabs.";
          })}
        >
          Copy to my screens
        </Btn>
      );
      break;
    case "plan": {
      const r = planR(embed);
      head = <>🎯 Trade plan · frozen {embed.frozenAt ? new Date(embed.frozenAt).toLocaleString() : ""}</>;
      body = (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
          <span><b>{embed.symbol}</b> {embed.direction}</span>
          <span>Entry <b style={{ color: "var(--qf-positive)" }}>{embed.entry}</b></span>
          <span>Stop <b style={{ color: "var(--qf-negative)" }}>{embed.stop}</b></span>
          {embed.targets.length > 0 && <span>Target <b>{embed.targets.join(" / ")}</b></span>}
          {r !== null && <span>R <b>{r}</b></span>}
        </div>
      );
      action = (
        <>
          <Btn small onClick={() => actions.onOpenSymbol(embed.symbol)}>Chart {embed.symbol}</Btn>
          <Btn
            small
            primary
            disabled={busy}
            onClick={run(async () => {
              const account = await fetchAccount();
              await savePlan({
                id: mintUlid(),
                accountId: account.id,
                symbol: embed.symbol,
                direction: embed.direction,
                entry: embed.entry,
                entryOrder: (embed.entryOrder as "limit" | "stop" | "market") ?? "limit",
                stop: embed.stop,
                targets: embed.targets.map((price, _i, all) => ({
                  id: mintUlid(),
                  price,
                  share: 1 / all.length,
                })),
                thesis: `Copied from ${author}'s post`,
              });
              return "Copied to your journal.";
            })}
          >
            Copy to my journal
          </Btn>
        </>
      );
      break;
    }
    case "tracklist":
      head = <>★ List · <b style={{ color: "var(--qf-text)" }}>{embed.name}</b> ({embed.symbols.length})</>;
      body = (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12 }}>
          {embed.symbols.slice(0, 40).map((s) => (
            <button key={s} type="button" style={CASHTAG} onClick={() => actions.onOpenSymbol(s)}>{s}</button>
          ))}
        </div>
      );
      action = (
        <Btn
          small
          primary
          disabled={busy}
          onClick={run(async () => {
            await createList({ id: mintUlid(), name: embed.name.slice(0, 40), symbols: embed.symbols });
            return "Copied to your lists.";
          })}
        >
          Copy to my lists
        </Btn>
      );
      break;
  }

  return (
    <div style={CARD}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", ...META }}>
        <span style={{ flex: 1, minWidth: 0 }}>{head}</span>
        {action}
      </div>
      {body && <div style={{ marginTop: 6 }}>{body}</div>}
      {note && <div style={{ ...META, marginTop: 4 }}>{note}</div>}
    </div>
  );
}

function ZoomImage({ src, alt }: { src: string; alt: string }) {
  const [big, setBig] = useState(false);
  return (
    <>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onClick={() => setBig(true)}
        style={{ width: "100%", maxHeight: 260, objectFit: "contain", display: "block", borderRadius: 4, cursor: "zoom-in", background: "var(--qf-background)" }}
      />
      {big && (
        <div
          role="dialog"
          aria-label={alt}
          onClick={() => setBig(false)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}
        >
          <img src={src} alt={alt} style={{ maxWidth: "95vw", maxHeight: "92vh" }} />
        </div>
      )}
    </>
  );
}

export const CARD: CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  background: "var(--qf-surface)",
  padding: 8,
  margin: "6px 0",
};
